import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface VisitorInfo {
  sessionId: string;
  page: string;
  referrer: string;
  device: string;
  country?: string;
  language: string;
  enteredAt: string;
  userAgent: string;
}

const getDeviceType = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return 'mobile';
  if (ua.includes('tablet') || ua.includes('ipad')) return 'tablet';
  return 'desktop';
};

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};

export const useVisitorPresence = () => {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const sessionId = useRef<string>(getSessionId());

  useEffect(() => {
    // Don't track on admin pages
    if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/interpreter')) {
      return;
    }

    const visitorInfo: VisitorInfo = {
      sessionId: sessionId.current,
      page: window.location.pathname,
      referrer: document.referrer || 'direct',
      device: getDeviceType(),
      language: navigator.language.split('-')[0],
      enteredAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    const channel = supabase.channel('visitors', {
      config: {
        presence: {
          key: sessionId.current,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        // Sync handled by admin component
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          try {
            await channel.track(visitorInfo);
          } catch {
            // Silently handle tracking errors to prevent console noise
          }
        }
      });

    channelRef.current = channel;

    // Update page on navigation
    const updatePage = () => {
      if (channelRef.current) {
        try {
          channelRef.current.track({
            ...visitorInfo,
            page: window.location.pathname,
          });
        } catch {
          // Silently handle tracking errors
        }
      }
    };

    window.addEventListener('popstate', updatePage);

    // Heartbeat to keep presence alive
    const heartbeat = setInterval(() => {
      if (channelRef.current) {
        try {
          channelRef.current.track({
            ...visitorInfo,
            page: window.location.pathname,
          });
        } catch {
          // Silently handle tracking errors
        }
      }
    }, 30000);

    return () => {
      window.removeEventListener('popstate', updatePage);
      clearInterval(heartbeat);
      if (channelRef.current) {
        channelRef.current.untrack();
        supabase.removeChannel(channelRef.current);
      }
    };
  }, []);
};
